import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { Crown, Sparkle, Wind, Star, Mountains } from '@phosphor-icons/react';
import { useAuth } from '@/lib/auth';
import FounderBadge from '@/components/FounderBadge';
import BloomButton from '@/components/BloomButton';
import ReportModal from '@/components/ReportModal';
import BlockButton from '@/components/BlockButton';

const RANK_COLORS = {
  'Carrier':         'text-white',
  'Explorer':        'text-bx-cyan',
  'Legend Maker':    'text-bx-volt',
  'Dream Architect': 'text-bx-magenta',
  'World Builder':   'text-bx-gold',
  'Origin Master':   'shimmer-text',
};

const CreatorProfile = () => {
  const { username } = useParams();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/creator/${username}`).then(({ data }) => setData(data)).catch(() => {});
  }, [username]);

  if (!data) return <div className="text-center py-20 text-white/65">loading creator…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="text-center mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan">creator profile</div>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-2">
          @{data.username}<FounderBadge username={data.username} size={20} className="ml-2" />
        </h1>
        <div className={`inline-flex items-center gap-1.5 mt-3 chip ${RANK_COLORS[data.rank] || ''}`}>
          <Crown size={12} weight="fill" /> {data.rank}
          {data.founder_number && <span className="text-bx-gold">· #{data.founder_number}</span>}
        </div>
        {me?.username && me.username !== data.username && (
          <div className="mt-4 flex flex-col items-center gap-3">
            <BloomButton recipientUsername={data.username} />
            <div className="flex items-center gap-4">
              <ReportModal subjectType="user" subjectId={data.user_id} label="Report" />
              <BlockButton targetUserId={data.user_id} targetUsername={data.username} />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <Stat icon={Sparkle} label="Living" value={data.created_living} color="text-bx-cyan" />
        <Stat icon={Mountains} label="Popped" value={data.created_popped} color="text-bx-magenta" />
        <Stat icon={Wind} label="Resonance" value={data.resonance_lifetime} color="text-bx-volt" />
        <Stat icon={Star} label="Stars" value={data.constellation.length} color="text-bx-gold" />
      </div>

      {/* Creator DNA */}
      <div className="grad-border p-5 mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-bx-magenta">creator dna</div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">signature trait</div>
            <div className="font-display font-bold mt-1">{data.dna.signature_trait}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">wanderlust</div>
            <div className="mt-1">
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-bx-cyan" style={{ width: `${(data.dna.wanderlust || 0) * 100}%` }} />
              </div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">mythic rate</div>
            <div className="font-display font-black text-bx-gold mt-1">{Math.round((data.dna.mythic_rate || 0) * 100)}%</div>
          </div>
        </div>
      </div>

      {/* Constellation */}
      <div>
        <h2 className="font-display text-2xl font-bold mb-3 flex items-center gap-2">
          <Star size={18} weight="fill" className="text-bx-gold" /> Constellation
        </h2>
        <div className="relative h-72 sm:h-96 rounded-2xl overflow-hidden" data-testid="constellation"
          style={{ background: 'radial-gradient(ellipse at top, #0a0014, #000)' }}>
          {data.constellation.length === 0 ? (
            <div className="absolute inset-0 grid place-items-center text-white/60 text-sm">
              Empty sky. Your popped bubbles will live here.
            </div>
          ) : data.constellation.map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0 }} animate={{ opacity: s.brightness }}
              transition={{ delay: i * 0.04 }}
              title={s.name}
              style={{ left: `${s.x * 100}%`, top: `${s.y * 100}%` }}
              className="absolute -translate-x-1/2 -translate-y-1/2 group">
              <span className={`block rounded-full bg-rar-${s.rarity || 'common'}`}
                style={{ width: `${4 + s.brightness * 8}px`, height: `${4 + s.brightness * 8}px`,
                         boxShadow: `0 0 ${6 + s.brightness * 12}px currentColor` }} />
              <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 text-[9px] text-white/60 whitespace-nowrap opacity-0 group-hover:opacity-100 transition">
                {s.name}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="glass rounded-xl p-4 text-center">
    <Icon size={14} weight="duotone" className={`${color} mx-auto`} />
    <div className={`font-display text-2xl font-black mt-1 ${color}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">{label}</div>
  </div>
);

export default CreatorProfile;
